import { t } from 'i18next';
import { isString } from 'lodash';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { localStorageGetItem } from 'src/utils/local-storage';

import i18n from 'src/locales/i18n';

import { BusinessErrorCodes } from 'src/types/business-errors';

export const generateCandidateProfileErrorMessage = (error: any) => i18n.t('profile.api.update.error');

// Mirror of the backend PLACEHOLDER_EMAIL_DOMAINS. @jobtetris-import.com is a legacy placeholder
// domain from an earlier import run; both must be treated as "no email".
export const PLACEHOLDER_EMAIL_DOMAINS = ['@placeholder.local', '@jobtetris-import.com'];

// Returns true when the email is absent or is a system-generated placeholder address.
// Shadow accounts and imported records with no email carry placeholder addresses, which are hidden
// from the UI (appear blank) and must never be offered as a mailto: target — the domains do not
// resolve, so sending to one bounces back at the sender.
export const isShadowEmail = (email: string | null | undefined): boolean =>
  !email || PLACEHOLDER_EMAIL_DOMAINS.some((domain) => email.endsWith(domain));

/**
 * Availability has two different empty states and they must not be rendered the same way (JT1-1620).
 *
 * The API omits `availability_status` entirely when the organisation does not have the past
 * candidate updates feature, so it arrives as `undefined`. When the feature IS available and the
 * candidate simply has not shared an availability, the field is present and `null`.
 *
 * Before JT1-1620 the column was non-null, so `undefined` was the only way to be empty and screens
 * could safely read "falsy" as "feature locked". That inference is now wrong: it puts a Feature
 * Locked chip in front of a candidate who has merely never answered the question.
 *
 * Callers must only ask this once the record has loaded. An absent record is also `undefined`, and
 * a half-loaded page is not a locked feature.
 */
export const isAvailabilityFeatureLocked = (
  status: string | null | undefined
): boolean => status === undefined;

export const generateCompanyInviteErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.CompanyInviteExists) {
      return i18n.t('candidates.invites.company_invite_modal.api.invite_exists');
    }
    if (e?.data.error_code === BusinessErrorCodes.CandidateAlreadyConnected) {
      return i18n.t('candidates.invites.company_invite_modal.api.already_connected');
    }
    if (e?.data.error_code === BusinessErrorCodes.InvalidCompanyInviteTarget) {
      return i18n.t('candidates.invites.company_invite_modal.api.invalid_target');
    }
    if (e?.data.error_code === BusinessErrorCodes.CandidateInviteExists) {
      return i18n.t('candidates.invites.company_invite_modal.api.candidate_invite_exists');
    }
    if (e?.data.error_code === BusinessErrorCodes.BadTenantOperation) {
      return i18n.t('candidates.invites.company_invite_modal.api.invalid_email_type');
    }
  }

  return i18n.t('candidates.invites.company_invite_modal.api.default_error');
}

export const generateCandidateCreateErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.CandidateCreateConflict) {
      return i18n.t('candidates.api.create_unlinked_candidate.tenant_linked_candidate_exists');
    }

    if (e?.data.error_code === BusinessErrorCodes.AccountTypeConflict) {
      return i18n.t('candidates.api.create_unlinked_candidate.account_type_conflict');
    }
  }

  return i18n.t('candidates.api.create_unlinked_candidate.default_error');
}

export const generateCandidateImportErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {

    if (e?.data.error_code === BusinessErrorCodes.InvalidFileType) {
      return i18n.t('upload.api.create.invalid_file_type');
    }

    if (e?.data.error_code === BusinessErrorCodes.ResumeMissingEmail) {
      return i18n.t('candidates.api.import_candidate.resume_missing_email_error');
    }

    if (e?.data.error_code === BusinessErrorCodes.ResumeClassificationFailed) {
      return i18n.t('candidates.api.import_candidate.resume_classification_error');
    }
  }

  return i18n.t('candidates.api.import_candidate.default_error');
}

export const generateCandidateUpdateErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.LinkedCandidateCannotBeEdited) {
      return i18n.t('candidates.api.update_unlinked_candidate.linked_candidate_restriction');
    }
  }

  return i18n.t('candidates.api.update_unlinked_candidate.default_error');
}

export const generateDirectCandidateInviteMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.CandidateInviteExists) {
      return i18n.t('candidates.api.create_direct_invite.already_invited');
    }

    if (e?.data.error_code === BusinessErrorCodes.CandidateAlreadyConnected) {
      return i18n.t('candidates.api.create_direct_invite.already_connected');
    }
  }

  return i18n.t('candidates.api.create_direct_invite.default_error');
}


export const generateUnlinkCandidateErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.InvalidCandidateType) {
      return i18n.t('candidates.api.unlink_candidate.invalid_type');
    }
  }

  return i18n.t('candidates.api.unlink_candidate.default_error');
}

export const generateRemoveCandidateErrorMessage = (e: any) => {

  if (e?.status === 403) {
    return i18n.t('exceptions.unauthorized');
  }

  if (isString(e?.data?.error_code)) {
    if (e?.data.error_code === BusinessErrorCodes.InvalidCandidateType) {
      return i18n.t('candidates.api.remove_candidate.invalid_type');
    }

    if (e?.data.error_code === BusinessErrorCodes.CandidateDeleteActiveApplications) {
      return i18n.t('candidates.api.remove_candidate.active_applications');
    }
  }

  return i18n.t('candidates.api.remove_candidate.default_error');
}

export const exportOrgCandidateProfile = async (candidateId: string) => {

  const toastId = enqueueSnackbar(t('candidates.export.api.loading'), {
    variant: 'loading',
    persist: true
  });

  try {

    // get the token from local storage
    const token = localStorageGetItem('access_token', undefined);

    // use fetch instead of rtk (doesn't fully support file download)
    const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/my/candidates/${candidateId}/profile/export`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/pdf'
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download');
    }

    // get the blob from the response
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `${candidateId}_profile.pdf`); // Or use candidate name if available

    document.body.appendChild(link);
    link.click();

    // cleanup
    link.remove();
    window.URL.revokeObjectURL(url);

    closeSnackbar(toastId);
    enqueueSnackbar(i18n.t('candidates.export.api.success'), { variant: 'success' });
  }
  catch (error) {
    closeSnackbar(toastId);
    enqueueSnackbar(i18n.t('candidates.export.api.default_error'), { variant: 'error' });
  }

};
